/**
 * Gemeinsame Sicht auf die Seiteneinrichtung – gleich, ob sie über
 * WordApiDesktop 1.3 oder über das Prüfmakro „Dokument auslesen“ stammt.
 */

import type { CheckResult } from "@/core/types";
import { mitErsatz } from "@/office/makro/pruefung";
import { formatAusMassen, readSeite } from "@/office/word/seite";
import { MAKRO_DOKUMENT } from "../makros";

export interface SeitenAngaben {
  spaltenMax: number;
  zeilennummern: boolean;
  ersteSeiteAnders: boolean;
  seitenrahmen: boolean;
  /** "A4", "A5", "Letter" … oder `null`, wenn unbekannt. */
  format: string | null;
  silbentrennung: boolean | null;
}

async function ausOfficeJs(): Promise<SeitenAngaben | null> {
  const seite = await readSeite();
  if (!seite) return null;
  const erster = seite.abschnitte[0];
  return {
    spaltenMax: Math.max(1, ...seite.abschnitte.map((abschnitt) => abschnitt.textColumns)),
    zeilennummern: seite.abschnitte.some((abschnitt) => abschnitt.lineNumbering),
    ersteSeiteAnders: seite.abschnitte.some((abschnitt) => abschnitt.differentFirstPage),
    seitenrahmen: seite.abschnitte.some((abschnitt) => abschnitt.seitenrahmen),
    format: erster
      ? (formatAusMassen(erster.pageWidth, erster.pageHeight) ??
        (erster.paperSize && erster.paperSize !== "Custom" ? erster.paperSize : null))
      : null,
    silbentrennung: seite.silbentrennung,
  };
}

/** Prüft die Seiteneinrichtung – Office.js zuerst, sonst über das Prüfmakro. */
export function pruefeSeite(bewerten: (angaben: SeitenAngaben) => CheckResult): Promise<CheckResult> {
  return mitErsatz(
    ausOfficeJs,
    MAKRO_DOKUMENT,
    (bericht) => ({
      spaltenMax: bericht.zahl("spalten") ?? 1,
      zeilennummern: bericht.janein("zeilennummern") === true,
      ersteSeiteAnders: bericht.janein("ersteSeiteAnders") === true,
      seitenrahmen: bericht.janein("seitenrahmen") === true,
      format: formatAusMassen(bericht.zahl("seiteBreite"), bericht.zahl("seiteHoehe")),
      silbentrennung: bericht.janein("silbentrennung"),
    }),
    bewerten,
  );
}
